import { useEffectWithoutCleanup } from "Shared/haunted/CustomHooks";
import { html, useRef } from "haunted";
import { HauntedFunc } from "Shared/haunted/HooksHelpers";
import { getParsedProperty, revealDelayedElements, toBoolean } from "Services/common";
import { ref } from "Components/directives/ref";
import classNames from "classnames";
import { useLoginWidget } from "./useLoginWidget";
import { TemplateResult } from "lit-html";
import { useHeaderDropdown } from "./useHeaderDropdown";
import { useAppInitializer } from "./useAppInitializer";
import { useHeaderScrollHandler } from "./useHeaderScrollHandler";
import { useMenubar } from "./useMenubar";
import { DynamicSettings } from "Components/admin/DynamicSettings";

export const useShadowDOM = false;
export const name = "ac-header";

export const observedAttributes: (keyof Attributes)[] = [
	"absolute-action",
	"booking-url",
	"culture",
	"dynamic-settings",
	"show-menu",
];

export interface Attributes {
	"absolute-action": string;
	"booking-url": string;
	"culture": string;
	"dynamic-settings": string;
	"show-menu": string;
}

export interface Props {
	absoluteAction: string;
	bookingUrl: string;
	culture: string;
	dynamicSettings: DynamicSettings;
	showMenu: boolean;
}

export const menuItemIds = ["offers-and-destinations", "my-booking", "benefits", "travel-information"] as const;
export type MenuItemId = (typeof menuItemIds)[number];

export interface MenuItemWithDropdownContent {
	id: MenuItemId;
	title: string;
	content: () => TemplateResult | string;
	mobileContent: () => TemplateResult | string;
}

export const Component: HauntedFunc<Props> = (host) => {
	const props: Props = {
		absoluteAction: host.absoluteAction,
		bookingUrl: host.bookingUrl,
		culture: host.culture,
		dynamicSettings:
			host.dynamicSettings && typeof host.dynamicSettings === "string"
				? getParsedProperty<DynamicSettings>(host.dynamicSettings)
				: undefined,
		showMenu: toBoolean(host.showMenu),
	};

	const root = useRef<HTMLDivElement>(undefined);
	const navBar = useRef<HTMLDivElement>(undefined);

	const loginWidget = useLoginWidget({
		absoluteAction: props.absoluteAction,
		bookingUrl: props.bookingUrl,
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
	});

	const scrollHandler = useHeaderScrollHandler({
		navbarElement: navBar.current,
		onScroll: loginWidget.close,
	});

	const dropdown = useHeaderDropdown({ culture: props.culture, isCollapsed: scrollHandler.isCollapsed });

	const menubar = useMenubar({
		absoluteAction: props.absoluteAction,
		activeMenuItemId: dropdown.activeId,
		bookingUrl: props.bookingUrl,
		culture: props.culture,
		dropdownMenus: dropdown.dropdownMenus,
		dynamicSettings: props.dynamicSettings,
		isCollapsed: scrollHandler.isCollapsed,
		showMenu: props.showMenu,
		closeDropdown: dropdown.closeMenu,
		closeLoginWidget: loginWidget.close,
		onRegisterAccountClick: loginWidget.registerAccountClick,
		openDropdown: dropdown.openMenu,
		toggleLoginWidget: loginWidget.toggle,
		toggleMenuItem: dropdown.toggleMenuItem,
	});

	const appInitializer = useAppInitializer({ closeMobileMenu: menubar.closeMobileMenu });

	const init = async () => {
		document.body.style.opacity = "0";

		await appInitializer.init();

		document.body.style.opacity = "1";
		revealDelayedElements(root.current);

		// eslint-disable-next-line no-console
		console.table(window.JetSmart.Variants);
	};

	useEffectWithoutCleanup(init, []);

	return html`
		<header
			ref=${ref(root)}
			class=${classNames("w-full fixed text-center z-[99998] common-transition", {
				"top-[-86px] overflow-hidden": scrollHandler.isCollapsed,
				"h-[55px] lg:h-[86px] top-0": !scrollHandler.isCollapsed,
			})}
			@mouseleave=${dropdown.closeMenu}
		>
			<div ref=${ref(navBar)}>${menubar.htmlTemplate()}</div>
			${dropdown.htmlTemplate()} ${loginWidget.htmlTemplate()}
		</header>
	`;
};
